/**
 * Settings.
 *
 * Three groups that look similar and are not. The company block feeds § 14
 * Abs. 4 UStG — get it wrong and every invoice is formally defective. The app
 * block holds the two decisions with real legal weight: which distance value
 * is the system of record, and how long a record stays editable. The driver
 * block writes to the account row, which lives outside the sealed envelopes.
 */

import { useEffect, useState } from 'react';
import {
  Callout,
  Card,
  Field,
  SectionHeading,
  Select,
  TextArea,
  TextInput,
} from '@/components/ui';
import { formatDateTime } from '@/domain/dates';
import { formatKm, parseKmToMeters } from '@/domain/money';
import {
  DEFAULT_APP_SETTINGS,
  type AppSettings,
  type CompanySettings,
  type UiLanguage,
  type Vehicle,
} from '@/domain/types';
import {
  saveAppSettings,
  saveCompanySettings,
  saveDriverIdentity,
  vehicleRepo,
} from '@/db/repositories';
import { useSession } from '@/session/SessionContext';
import { GEOAPIFY_KEY_HELP } from '@/geo/geoapify';
import { LANGUAGE_NAMES, useLanguage, useT } from '@/i18n';
import type { Dictionary } from '@/i18n';

type Tab = 'firma' | 'rechnung' | 'app' | 'fahrzeuge';

const TABS: Array<{ id: Tab; label: (t: Dictionary) => string }> = [
  { id: 'firma', label: (t) => t.settings.tabCompany },
  { id: 'rechnung', label: (t) => t.settings.tabInvoice },
  { id: 'app', label: (t) => t.settings.tabApp },
  { id: 'fahrzeuge', label: (t) => t.settings.tabVehicles },
];

export function SettingsScreen() {
  const session = useSession();
  const t = useT();
  const { setLanguage } = useLanguage();
  const [tab, setTab] = useState<Tab>('firma');
  const [company, setCompany] = useState<CompanySettings>(session.company);
  const [app, setApp] = useState<AppSettings>(session.app);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setCompany(session.company), [session.company]);
  useEffect(() => setApp(session.app), [session.app]);

  const dirty =
    JSON.stringify(company) !== JSON.stringify(session.company) ||
    JSON.stringify(app) !== JSON.stringify(session.app);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await saveCompanySettings(session.audit, company);
      await saveAppSettings(session.audit, app);
      await session.refresh();
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.common.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  function patchCompany(changes: Partial<CompanySettings>) {
    setCompany((current) => ({ ...current, ...changes }));
  }

  function patchApp(changes: Partial<AppSettings>) {
    setApp((current) => ({ ...current, ...changes }));
  }

  /**
   * The language applies at once — waiting for a save would make the control
   * feel broken — and is written straight through so the encrypted settings
   * never disagree with `localStorage`. Whatever else is pending on the tab is
   * committed with it rather than being silently discarded by the refresh.
   */
  async function switchLanguage(next: UiLanguage) {
    setLanguage(next);
    const merged = { ...app, language: next };
    setApp(merged);
    await saveAppSettings(session.audit, merged);
    await session.refresh();
  }

  return (
    <div className="space-y-5">
      <SectionHeading title={t.settings.title} description={t.settings.description} />

      <nav className="border-line flex gap-1 overflow-x-auto border-b pb-px">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setTab(entry.id)}
            className={`rounded-t-md px-3 py-2 text-sm whitespace-nowrap transition-colors ${
              tab === entry.id
                ? 'border-accent text-heading border-b-2 font-medium'
                : 'text-muted hover:text-heading border-b-2 border-transparent'
            }`}
          >
            {entry.label(t)}
          </button>
        ))}
      </nav>

      {error && <Callout tone="danger">{error}</Callout>}
      {saved && <Callout tone="success">{t.settings.saved}</Callout>}

      {tab === 'firma' && <CompanyTab company={company} onChange={patchCompany} />}
      {tab === 'rechnung' && <InvoiceTab company={company} onChange={patchCompany} />}
      {tab === 'app' && <AppTab app={app} onChange={patchApp} onLanguage={switchLanguage} />}
      {tab === 'fahrzeuge' && <VehicleTab />}

      {tab !== 'fahrzeuge' && (
        <div className="bg-canvas/90 border-line sticky bottom-0 -mx-4 flex items-center gap-3 border-t px-4 py-3 backdrop-blur">
          <button
            type="button"
            className="btn-primary"
            onClick={() => void save()}
            disabled={busy || !dirty}
          >
            {busy ? t.common.saving : t.common.save}
          </button>
          {dirty && <span className="text-muted text-xs">{t.settings.unsaved}</span>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function CompanyTab({
  company,
  onChange,
}: {
  company: CompanySettings;
  onChange: (changes: Partial<CompanySettings>) => void;
}) {
  const t = useT();

  return (
    <>
      <Card title={t.settings.companyCard}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t.settings.companyName} required hint="§ 14 Abs. 4 Nr. 1 UStG">
            <TextInput
              value={company.companyName}
              onChange={(event) => onChange({ companyName: event.target.value })}
            />
          </Field>
          <Field label={t.settings.ownerName}>
            <TextInput
              value={company.ownerName}
              onChange={(event) => onChange({ ownerName: event.target.value })}
            />
          </Field>
          <Field label={t.settings.street} required>
            <TextInput
              value={company.street}
              onChange={(event) => onChange({ street: event.target.value })}
            />
          </Field>
          <div className="grid grid-cols-[100px_minmax(0,1fr)] gap-3">
            <Field label={t.settings.postalCode} required>
              <TextInput
                value={company.postalCode}
                onChange={(event) => onChange({ postalCode: event.target.value })}
                inputMode="numeric"
              />
            </Field>
            <Field label={t.settings.city} required>
              <TextInput
                value={company.city}
                onChange={(event) => onChange({ city: event.target.value })}
              />
            </Field>
          </div>
          <Field label={t.settings.phone}>
            <TextInput
              value={company.phone ?? ''}
              onChange={(event) => onChange({ phone: event.target.value || undefined })}
              inputMode="tel"
            />
          </Field>
          <Field label={t.settings.email} required hint={t.settings.emailHint}>
            <TextInput
              type="email"
              value={company.email ?? ''}
              onChange={(event) => onChange({ email: event.target.value || undefined })}
            />
          </Field>
        </div>
      </Card>

      <Card title={t.settings.taxCard}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t.settings.taxNumber} hint={t.settings.taxNumberHint}>
            <TextInput
              value={company.taxNumber ?? ''}
              onChange={(event) => onChange({ taxNumber: event.target.value || undefined })}
              placeholder="143/815/08152"
            />
          </Field>
          <Field label={t.settings.vatId}>
            <TextInput
              value={company.vatId ?? ''}
              onChange={(event) => onChange({ vatId: event.target.value || undefined })}
              placeholder="DE123456789"
            />
          </Field>
          <Field label={t.settings.registrationNumber} hint={t.settings.registrationHint}>
            <TextInput
              value={company.registrationNumber ?? ''}
              onChange={(event) =>
                onChange({ registrationNumber: event.target.value || undefined })
              }
              placeholder="HRB 123456"
            />
          </Field>
          <Field label={t.settings.licenceType} required>
            <Select
              value={company.licenceType}
              onChange={(event) =>
                onChange({ licenceType: event.target.value as CompanySettings['licenceType'] })
              }
            >
              <option value="mietwagen">{t.settings.licenceMietwagen}</option>
              <option value="taxi">{t.settings.licenceTaxi}</option>
            </Select>
          </Field>
          <Field label={t.settings.kleinunternehmer}>
            <Select
              value={company.kleinunternehmer ? 'ja' : 'nein'}
              onChange={(event) => onChange({ kleinunternehmer: event.target.value === 'ja' })}
            >
              <option value="nein">{t.settings.kleinunternehmerNo}</option>
              <option value="ja">{t.settings.kleinunternehmerYes}</option>
            </Select>
          </Field>
        </div>

        <Callout tone={company.licenceType === 'taxi' ? 'info' : 'neutral'}>
          {company.licenceType === 'taxi' ? t.settings.taxiNote : t.settings.mietwagenNote}
        </Callout>
      </Card>

      <Card title={t.settings.bankCard}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t.settings.bankName}>
            <TextInput
              value={company.bankName ?? ''}
              onChange={(event) => onChange({ bankName: event.target.value || undefined })}
            />
          </Field>
          <Field label={t.settings.iban}>
            <TextInput
              value={company.iban ?? ''}
              onChange={(event) => onChange({ iban: event.target.value || undefined })}
              placeholder="DE02 1203 0000 0000 2020 51"
            />
          </Field>
          <Field label={t.settings.bic}>
            <TextInput
              value={company.bic ?? ''}
              onChange={(event) => onChange({ bic: event.target.value || undefined })}
            />
          </Field>
        </div>
      </Card>
    </>
  );
}

function InvoiceTab({
  company,
  onChange,
}: {
  company: CompanySettings;
  onChange: (changes: Partial<CompanySettings>) => void;
}) {
  const t = useT();
  const [logoError, setLogoError] = useState<string | null>(null);

  async function readLogo(file: File) {
    setLogoError(null);
    if (!/^image\/(png|jpeg)$/.test(file.type)) {
      setLogoError(t.settings.logoTypeError);
      return;
    }
    if (file.size > 500_000) {
      setLogoError(t.settings.logoSizeError);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange({ logoDataUrl: String(reader.result) });
    reader.readAsDataURL(file);
  }

  return (
    <Card title={t.settings.invoiceCard}>
      <div className="space-y-4">
        <Field label={t.settings.logo} hint={t.settings.logoHint}>
          <div className="flex flex-wrap items-center gap-3">
            {company.logoDataUrl && (
              <img
                src={company.logoDataUrl}
                alt={t.settings.logo}
                className="border-line h-12 rounded border bg-white object-contain px-2"
              />
            )}
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void readLogo(file);
              }}
              className="text-muted text-xs file:mr-3 file:rounded-md file:border-0 file:bg-subtle file:px-3 file:py-1.5 file:text-body"
            />
            {company.logoDataUrl && (
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => onChange({ logoDataUrl: undefined })}
              >
                {t.common.remove}
              </button>
            )}
          </div>
        </Field>
        {logoError && <Callout tone="warning">{logoError}</Callout>}

        <Field label={t.settings.accentColor}>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={company.invoiceAccentColor}
              onChange={(event) => onChange({ invoiceAccentColor: event.target.value })}
              className="border-line-strong h-9 w-14 cursor-pointer rounded border bg-transparent"
            />
            <TextInput
              value={company.invoiceAccentColor}
              onChange={(event) => onChange({ invoiceAccentColor: event.target.value })}
              className="w-32 font-mono text-sm"
            />
          </div>
        </Field>

        <Field label={t.settings.paymentTerms} hint={t.settings.paymentTermsHint}>
          <TextInput
            value={company.invoicePaymentTerms}
            onChange={(event) => onChange({ invoicePaymentTerms: event.target.value })}
          />
        </Field>

        <Field label={t.settings.footerNote} hint={t.settings.footerNoteHint}>
          <TextArea
            rows={2}
            value={company.invoiceFooterNote ?? ''}
            onChange={(event) => onChange({ invoiceFooterNote: event.target.value || undefined })}
          />
        </Field>
      </div>
    </Card>
  );
}

function AppTab({
  app,
  onChange,
  onLanguage,
}: {
  app: AppSettings;
  onChange: (changes: Partial<AppSettings>) => void;
  onLanguage: (next: UiLanguage) => Promise<void>;
}) {
  const t = useT();
  const [toleranceInput, setToleranceInput] = useState(
    formatKm(app.reconciliationToleranceMeters),
  );

  useEffect(
    () => setToleranceInput(formatKm(app.reconciliationToleranceMeters)),
    [app.reconciliationToleranceMeters],
  );

  return (
    <>
      <DriverCard language={app.language} onLanguage={onLanguage} />

      <Card title={t.settings.distanceCard}>
        <Field label={t.settings.distanceAuthority}>
          <Select
            value={app.distanceSystemOfRecord}
            onChange={(event) =>
              onChange({
                distanceSystemOfRecord: event.target
                  .value as AppSettings['distanceSystemOfRecord'],
              })
            }
          >
            <option value="computed">{t.settings.distanceComputed}</option>
            <option value="odometer">{t.settings.distanceOdometer}</option>
          </Select>
        </Field>

        <Callout tone="warning" title={t.settings.kassenSichVTitle}>
          {t.settings.kassenSichVBody}
        </Callout>
      </Card>

      <Card title={t.settings.geoCard}>
        <div className="space-y-4">
          <Field label={t.settings.geoKey} hint={GEOAPIFY_KEY_HELP}>
            <TextInput
              value={app.geoapifyApiKey ?? ''}
              onChange={(event) => onChange({ geoapifyApiKey: event.target.value || undefined })}
              placeholder={t.settings.geoKeyPlaceholder}
              className="font-mono text-sm"
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t.settings.geoLat}>
              <TextInput
                value={String(app.geocodeBiasLat)}
                onChange={(event) =>
                  onChange({ geocodeBiasLat: Number(event.target.value) || app.geocodeBiasLat })
                }
                inputMode="decimal"
              />
            </Field>
            <Field label={t.settings.geoLon}>
              <TextInput
                value={String(app.geocodeBiasLon)}
                onChange={(event) =>
                  onChange({ geocodeBiasLon: Number(event.target.value) || app.geocodeBiasLon })
                }
                inputMode="decimal"
              />
            </Field>
            <Field label={t.settings.geoCountry}>
              <TextInput
                value={app.geocodeCountryCode}
                onChange={(event) => onChange({ geocodeCountryCode: event.target.value })}
                maxLength={2}
              />
            </Field>
          </div>
        </div>
      </Card>

      <Card title={t.settings.lockCard}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t.settings.lockDays} hint={t.settings.lockDaysHint}>
            <TextInput
              type="number"
              min={1}
              max={90}
              value={app.recordLockDays}
              onChange={(event) =>
                onChange({
                  recordLockDays: Number(event.target.value) || DEFAULT_APP_SETTINGS.recordLockDays,
                })
              }
            />
          </Field>
          <Field label={t.settings.tolerance} hint={t.settings.toleranceHint}>
            <TextInput
              value={toleranceInput}
              onChange={(event) => {
                setToleranceInput(event.target.value);
                const meters = parseKmToMeters(event.target.value);
                if (meters !== null) onChange({ reconciliationToleranceMeters: meters });
              }}
              inputMode="decimal"
            />
          </Field>
        </div>
      </Card>

      <Card title={t.settings.backupCard}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t.settings.warnAfter}>
            <TextInput
              type="number"
              min={1}
              value={app.backupWarnAfterDays}
              onChange={(event) =>
                onChange({
                  backupWarnAfterDays:
                    Number(event.target.value) || DEFAULT_APP_SETTINGS.backupWarnAfterDays,
                })
              }
            />
          </Field>
          <Field label={t.settings.blockAfter}>
            <TextInput
              type="number"
              min={1}
              value={app.backupBlockAfterDays}
              onChange={(event) =>
                onChange({
                  backupBlockAfterDays:
                    Number(event.target.value) || DEFAULT_APP_SETTINGS.backupBlockAfterDays,
                })
              }
            />
          </Field>
        </div>
        <p className="text-muted mt-3 text-xs">
          {app.lastBackupAt
            ? t.settings.lastBackupAt(formatDateTime(app.lastBackupAt))
            : t.settings.neverBackedUp}
        </p>
      </Card>
    </>
  );
}

/**
 * Driver identity and interface language.
 *
 * The two name fields write to the account row rather than to settings, so
 * they save on their own button instead of the shared footer.
 */
function DriverCard({
  language,
  onLanguage,
}: {
  language: UiLanguage;
  onLanguage: (next: UiLanguage) => Promise<void>;
}) {
  const session = useSession();
  const t = useT();

  const [displayName, setDisplayName] = useState(session.account.displayName);
  const [driverCode, setDriverCode] = useState(session.account.driverCode);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDisplayName(session.account.displayName);
    setDriverCode(session.account.driverCode);
  }, [session.account]);

  const dirty =
    displayName !== session.account.displayName || driverCode !== session.account.driverCode;

  async function saveDriver() {
    setBusy(true);
    setError(null);
    try {
      if (!displayName.trim()) throw new Error(t.settings.driverNameRequired);
      await saveDriverIdentity(session.audit, session.account.id, { displayName, driverCode });
      await session.refresh();
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.common.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t.settings.driverCard}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t.settings.displayName} required>
          <TextInput
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </Field>
        <Field label={t.settings.driverCode} hint={t.settings.driverCodeHint}>
          <TextInput
            value={driverCode}
            onChange={(event) => setDriverCode(event.target.value)}
          />
        </Field>
      </div>

      {error && <Callout tone="danger">{error}</Callout>}
      {saved && <Callout tone="success">{t.settings.driverSaved}</Callout>}

      <button
        type="button"
        className="btn-secondary mt-3"
        onClick={() => void saveDriver()}
        disabled={busy || !dirty}
      >
        {busy ? t.common.saving : t.settings.saveDriver}
      </button>

      <div className="border-line mt-4 border-t pt-4">
        <Field label={t.settings.language} hint={t.settings.languageHint}>
          <Select
            value={language}
            onChange={(event) => void onLanguage(event.target.value as UiLanguage)}
          >
            <option value="de">{LANGUAGE_NAMES.de}</option>
            <option value="en">{LANGUAGE_NAMES.en}</option>
          </Select>
        </Field>
      </div>
    </Card>
  );
}

function VehicleTab() {
  const session = useSession();
  const t = useT();
  const [draft, setDraft] = useState({ vehicleCode: '', licensePlate: '', make: '', model: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      if (!draft.licensePlate.trim()) throw new Error(t.settings.errPlate);
      const vehicle: Vehicle = {
        id: crypto.randomUUID(),
        vehicleCode:
          draft.vehicleCode.trim() || String(session.vehicles.length + 1).padStart(2, '0'),
        licensePlate: draft.licensePlate.trim().toUpperCase(),
        make: draft.make.trim(),
        model: draft.model.trim(),
        active: true,
        createdAt: new Date().toISOString(),
      };
      await vehicleRepo.create(session.audit, vehicle);
      await session.refreshMasterData();
      setDraft({ vehicleCode: '', licensePlate: '', make: '', model: '' });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.settings.createFailed);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(vehicle: Vehicle) {
    await vehicleRepo.update(session.audit, vehicle.id, { active: !vehicle.active });
    await session.refreshMasterData();
  }

  return (
    <>
      <Card title={t.settings.vehiclesCard}>
        <p className="text-muted mb-3 text-xs">{t.settings.vehiclesNote}</p>
        {session.vehicles.length === 0 ? (
          <p className="text-muted text-sm">{t.settings.noVehicles}</p>
        ) : (
          <ul className="divide-line divide-y">
            {session.vehicles.map((vehicle) => (
              <li key={vehicle.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-heading text-sm">
                    {vehicle.licensePlate}
                    <span className="text-muted">
                      {t.settings.vehicleNumber(vehicle.vehicleCode)}
                    </span>
                  </p>
                  <p className="text-muted truncate text-xs">
                    {[vehicle.make, vehicle.model].filter(Boolean).join(' ') || t.common.dash}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-ghost shrink-0 text-xs"
                  onClick={() => void toggleActive(vehicle)}
                >
                  {vehicle.active ? t.settings.deactivate : t.settings.reactivate}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={t.settings.addVehicle}>
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Field label={t.settings.licensePlate} required>
            <TextInput
              value={draft.licensePlate}
              onChange={(event) => setDraft({ ...draft, licensePlate: event.target.value })}
              placeholder="M-XY 1234"
            />
          </Field>
          <Field label={t.settings.vehicleCode} hint={t.settings.vehicleCodeHint}>
            <TextInput
              value={draft.vehicleCode}
              onChange={(event) => setDraft({ ...draft, vehicleCode: event.target.value })}
            />
          </Field>
          <Field label={t.settings.make}>
            <TextInput
              value={draft.make}
              onChange={(event) => setDraft({ ...draft, make: event.target.value })}
              placeholder="Mercedes-Benz"
            />
          </Field>
          <Field label={t.settings.model}>
            <TextInput
              value={draft.model}
              onChange={(event) => setDraft({ ...draft, model: event.target.value })}
              placeholder="V-Klasse"
            />
          </Field>
        </div>
        <button
          type="button"
          className="btn-primary mt-4"
          onClick={() => void add()}
          disabled={busy}
        >
          {busy ? t.settings.creatingVehicle : t.settings.createVehicle}
        </button>
      </Card>
    </>
  );
}
